'use client';

import type { Product } from '@repo/shopify';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { buildViewItemListEvent, trackAnalyticsEvent } from '@/lib/analytics/client';

interface CollectionAnalyticsProps {
	collectionId: string;
	collectionHandle: string;
	collectionTitle: string;
	products: Product[];
	/**
	 * For the always-mounted DEFAULT grid (collection-results-host.tsx): skip
	 * the view event when the URL carries sort/filter params, because the
	 * filtered results section (hidden-grid sibling) reports that view.
	 */
	skipWhenFiltered?: boolean;
}

function hasCollectionFilterParams(searchParams: URLSearchParams) {
	for (const key of searchParams.keys()) {
		if (key === 'sort' || key === 'after' || key.startsWith('filter.')) return true;
	}
	return false;
}

export function CollectionAnalytics({
	collectionId,
	collectionHandle,
	collectionTitle,
	products,
	skipWhenFiltered = false,
}: CollectionAnalyticsProps) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const searchParamsString = searchParams.toString();
	const skip = skipWhenFiltered && hasCollectionFilterParams(searchParams);
	const trackedUrlRef = useRef<string | null>(null);

	useEffect(() => {
		if (skip) return;
		const trackedUrl = searchParamsString ? `${pathname}?${searchParamsString}` : pathname;
		if (trackedUrlRef.current === trackedUrl) return;

		trackedUrlRef.current = trackedUrl;
		trackAnalyticsEvent(
			buildViewItemListEvent({ collectionId, collectionHandle, collectionTitle, products }),
		);
	}, [
		collectionHandle,
		collectionId,
		collectionTitle,
		pathname,
		products,
		searchParamsString,
		skip,
	]);

	return null;
}
